import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import VerboLogo from './VerboLogo';

interface BetaOverlayProps {
  message: string;
  onAccept: () => void;
}

export const BetaOverlay: React.FC<BetaOverlayProps> = ({ message, onAccept }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has already accepted the beta notice in this session
    const hasAccepted = sessionStorage.getItem('beta_accepted');
    if (!hasAccepted) {
      setIsOpen(true);
    }
  }, []);

  const handleContinue = () => {
    sessionStorage.setItem('beta_accepted', 'true');
    setIsOpen(false);
    onAccept();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-app-bg/95 backdrop-blur-xl"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="w-full max-w-md bg-card-bg border border-grid-line shadow-2xl rounded-[2.5rem] p-8 space-y-8 relative overflow-hidden"
          >
            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary-accent/5 rounded-full -mr-16 -mt-16 blur-3xl pointer-events-none" />

            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 bg-primary-accent/10 rounded-3xl flex items-center justify-center p-3 relative">
                <VerboLogo size={40} />
                <div className="absolute -top-1 -right-1">
                  <span className="flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-accent opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-primary-accent"></span>
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-accent/10 border border-primary-accent/20 rounded-full">
                  <AlertCircle size={10} className="text-primary-accent" />
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-primary-accent">Versión Preliminar</span>
                </div>
                <h2 className="text-2xl font-black text-text-primary tracking-tight">¡Bienvenido a la Beta!</h2>
              </div>

              <div className="bg-board-bg/50 border border-grid-line p-5 rounded-2xl">
                <p className="text-sm text-text-secondary leading-relaxed font-medium">
                  {message}
                </p>
              </div>

              <div className="flex items-center gap-4 text-left w-full px-2">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} className="text-emerald-500" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-black uppercase tracking-wider text-text-primary">Acceso Seguro</p>
                  <p className="text-[10px] text-text-muted font-medium">Tus datos están protegidos por SSL y Firebase Auth.</p>
                </div>
              </div>

              <button
                onClick={handleContinue}
                className="w-full bg-primary-accent hover:opacity-90 text-white font-black py-4.5 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-primary-accent/20 uppercase text-xs tracking-[0.2em]"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
